import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LayerDefinitionDc, AttributeDefinitionDc } from "@evergis/api";

import { usePostgresLayer } from "../layer/usePostgresLayer";
import { validFieldTypes } from "../../evergis/core/attributes/fieldTypes";
import { createAttributeOption, AttributeOption } from "../../evergis/core/attributes/createAttributeOption";

type AttributesListParams = {
  withDefault?: boolean;
  excludeSystemAttrs?: boolean;
  includeHidden?: boolean;
};

export const isValidAttribute = (attribute: AttributeDefinitionDc) => validFieldTypes.includes(attribute.type);

export const collectLayerAttributeOptions = (
  layerDefinition: LayerDefinitionDc,
  includeHidden?: boolean,
): AttributeOption[] => {
  const { attributes } = layerDefinition;

  return Object.entries(attributes || []).reduce((options, [name, attribute]) => {
    if (isValidAttribute(attribute) && (includeHidden || attribute.isDisplayed)) {
      const attributeOption = createAttributeOption({ name, ...attribute });

      options.push(attributeOption);
    }

    return options;
  }, []);
};

export const useLayerAttributeOptions = (
  layerName: string,
  { withDefault, excludeSystemAttrs, includeHidden }: AttributesListParams = {},
): AttributeOption[] => {
  const { t } = useTranslation("common");

  const {
    postgresLayer: { layerDefinition, layerAttributes, idAttribute },
  } = usePostgresLayer(layerName, { waitInput: true });

  return useMemo<AttributeOption[]>((): AttributeOption[] => {
    if (!layerDefinition) {
      return [];
    }

    let attributeOptions = collectLayerAttributeOptions(layerDefinition, includeHidden);

    if (excludeSystemAttrs) {
      const excludeAttrs = layerAttributes
        .filter(({ columnName }) => !columnName)
        .map(({ attributeName }) => attributeName);

      attributeOptions = attributeOptions.filter(({ value }) => !excludeAttrs.includes(value));
    }

    if (withDefault) {
      return [{ value: null, text: t("notChosen") } as AttributeOption, ...attributeOptions];
    }

    return attributeOptions;
  }, [layerDefinition, idAttribute, layerAttributes, includeHidden, excludeSystemAttrs, withDefault, t]);
};
